import { useCallback } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { space, borderRadius, fontFamily, fontSize } from '@core-ds/primitives';
import Language from './language';
import { CardLink } from './card_dropdown_parts';

const LanguageButton = styled(CardLink)`
   &:hover > .iso-code {
      color: ${({ theme }) => theme.isoCode.hover.font};
      background-color: ${({ theme }) => theme.isoCode.hover.background};
      border-color: ${({ theme }) => theme.isoCode.hover.border};
   }

   &:hover > .language-name {
      color: ${({ theme }) => theme.languageNameHover};
   }
`;

interface LanguageIsoCodeProps {
   $isSelected: boolean;
}

const LanguageIsoCode = styled.div<LanguageIsoCodeProps>`
   display: flex;
   color: ${({ $isSelected, theme }) => ($isSelected ? theme.isoCode.selected.font : theme.mainText)};
   background-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.isoCode.selected.background : 'transparent'};
   line-height: 13px;
   font-size: ${fontSize.sm};
   font-family: ${fontFamily.monoSystem};
   margin-right: ${space[4]};
   padding: 1px ${space[1]};
   border: 1px solid
      ${({ $isSelected, theme }) =>
         $isSelected ? theme.isoCode.selected.border : theme.isoCode.standard.border};
   border-radius: ${borderRadius.sm};
`;

const LanguageName = styled.div`
   display: flex;
   color: ${({ theme }) => theme.mainText};
   font-size: ${({ theme }) => theme.fontSize};
   line-height: inherit;
`;

interface LanguageCardProps {
   language: Language;
   className?: string;
   onClickLanguage: (langid: string) => void;
}

const LanguageCard: FunctionComponent<LanguageCardProps> = ({
   language,
   onClickLanguage,
   className,
}) => {
   const { isoCode, autoglottonym, isSelected } = language;
   const langid = isoCode.toLowerCase();

   const buttonClicked = useCallback(() => onClickLanguage(langid), [onClickLanguage, langid]);

   return (
      <LanguageButton href="#" className={className} data-langid={langid} onClick={buttonClicked}>
         <LanguageIsoCode className="iso-code" $isSelected={isSelected}>
            {isoCode}
         </LanguageIsoCode>
         <LanguageName className="language-name">{autoglottonym}</LanguageName>
      </LanguageButton>
   );
};

export default LanguageCard;
